/* PDP display prices use the selected variant's presentment allocation, never widget money. */
(() => {
  const cents = value => Number.isSafeInteger(value) && value >= 0;
  const validId = value => (typeof value === 'string' && /^[1-9]\d*$/.test(value)) ||
    (typeof value === 'number' && Number.isSafeInteger(value) && value > 0);

  /** @param {unknown} source @param {string[]} sampleIds @returns {object|null} */
  function priceData(source, sampleIds = []) {
    if (!source || !Array.isArray(source.variants) || typeof source.currency !== 'string' || !/^[A-Z]{3}$/.test(source.currency)) return null;
    const excluded = new Set(sampleIds.map(String));
    const variants = source.variants.filter(variant => variant && validId(variant.id) && cents(variant.price) &&
      (variant.compareAtPrice == null || cents(variant.compareAtPrice))).map(variant => ({
      ...variant,
      id: String(variant.id),
      compareAtPrice: variant.compareAtPrice || 0,
      plans: excluded.has(String(variant.id)) ? [] : (Array.isArray(variant.plans) ? variant.plans : [])
        .filter(plan => plan && validId(plan.id) && cents(plan.price)).map(plan => ({ ...plan, id: String(plan.id) })),
    }));
    if (variants.length !== source.variants.length || new Set(variants.map(variant => variant.id)).size !== variants.length) return null;
    return { currency: source.currency, locale: source.locale || 'en', handle: typeof source.handle === 'string' ? source.handle : '', variants };
  }

  /** @param {object} variant @param {object} state @returns {{price:number,compare:number}|null} */
  function variantQuote(variant, state) {
    if (!variant) return null;
    let price = variant.price;
    if (state.mode === 'subscription') {
      const allocation = variant.plans.find(plan => plan.id === String(state.planId));
      if (!allocation) return null;
      price = allocation.price;
    } else if (state.mode === 'one-time') {
      if (cents(state.merchantCents) && state.merchantCents > 0) price = state.merchantCents;
      else if (Number.isFinite(state.campaignRate) && state.campaignRate > 0 && state.campaignRate < 100) {
        price = Math.round(price * (100 - state.campaignRate) / 100);
      }
      else if (Number.isFinite(state.summerRate) && state.summerRate > 0 && state.summerRate <= 100) {
        price -= Math.floor(price * state.summerRate / 100);
      }
    } else return null;
    const original = Math.max(variant.price, variant.compareAtPrice || 0);
    return { price, compare: price < original ? original : 0 };
  }

  /** @param {object|null} data @param {object} state @returns {object|null} */
  function selectedQuote(data, state) {
    if (!data || !data.variants.some(item => item.plans.length)) return null;
    const variant = data.variants.find(item => item.id === String(state.variantId));
    if (!variant) return null;
    const quote = variantQuote(variant, state);
    return quote && { ...quote, variant };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { priceData, variantQuote, selectedQuote };
  if (typeof customElements === 'undefined' || customElements.get('beleaev-subscription-prices')) return;

  class SubscriptionPrices extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }
    initialize() {
      if (!this.isConnected || this.abort || this.closest('quick-add-modal')) return;
      this.root = this.closest('product-info, [id^="ProductInfo-"]');
      const node = this.root?.querySelector('beleaev-purchase-options script[type="application/json"]');
      if (!node) return;
      let source;
      try { source = JSON.parse(node.textContent); } catch (error) { return; }
      const sampleIds = this.root.querySelector('beleaev-caviar-sample-rule')?.dataset.variantIds?.split(',') || [];
      this.data = priceData(source, sampleIds);
      if (!this.data || !this.data.variants.some(variant => variant.plans.length)) return;
      try { this.formatter = new Intl.NumberFormat(this.data.locale, { style: 'currency', currency: this.data.currency }); }
      catch (error) { this.formatter = new Intl.NumberFormat('en', { style: 'currency', currency: this.data.currency }); }
      this.data = { ...this.data, locale: this.formatter.resolvedOptions().locale };
      this.originals = new Map();
      this.abort = new AbortController();
      ['change', 'input', 'beleaev:purchase-change', 'beleaev:purchase-state-sync'].forEach(name =>
        this.root.addEventListener(name, () => this.schedule(), { signal: this.abort.signal }));
      this.root.addEventListener('click', event => {
        if (event.target.closest('.variant-label, .appstle_sub_widget')) this.schedule();
      }, { capture: true, signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, { childList: true, characterData: true, subtree: true, attributes: true,
        attributeFilter: ['checked', 'value', 'disabled', 'class'] });
      this.update(); setTimeout(() => this.schedule(), 500); setTimeout(() => this.schedule(), 2000);;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.release();
      this.abort = null;
    }
    schedule() {
      if (this.pending) return;
      this.pending = true;
      queueMicrotask(() => { this.pending = false; if (this.isConnected) this.update(); });
    }
    money(value) { return this.formatter.format(value / 100); }
    primaryForms(selected) {
      const id = selected?.getAttribute('form');
      if (!id || !this.root.dataset.productId) return [];
      return Array.from(this.root.querySelectorAll('form[action*="/cart/add"]')).filter(form =>
        form.id === id && form.querySelector('input[name="product-id"]')?.value === this.root.dataset.productId);
    }
    purchaseState(selected) {
      const widget = this.root.querySelector('.appstle_sub_widget');
      const oneTime = widget?.querySelector('.appstle_one_time_details_wrapper input[type="radio"]:checked:not(:disabled)');
      const subscription = widget?.querySelector('.appstle_subscription_radio_wrapper input[type="radio"]:checked:not(:disabled)');
      if (oneTime && !subscription) return { mode: 'one-time', planId: '' };
      const planSelect = widget?.querySelector('select[id^="appstle_selling_plan"], .appstle_subscription_radio_wrapper select');
      const selectId = planSelect && !planSelect.disabled ? planSelect.value : '';
      const variant = this.data.variants.find(item => item.id === selected?.value);
      const radioId = variant?.plans.some(plan => plan.id === subscription?.value) ? subscription.value : '';
      const formId = this.primaryForms(selected).flatMap(form => Array.from(form.querySelectorAll('input[name="selling_plan"]')))
        .find(input => !input.disabled && input.value)?.value || '';
      const planId = selectId || radioId || formId;
      if (subscription || (!oneTime && formId)) return { mode: 'subscription', planId };
      return { mode: '', planId: '' };
    }
    summerRates() {
      const active = new URLSearchParams(location.search).get('summer') === '1' ||
        (Date.now() >= Date.parse('2026-08-13T23:00:00Z') && Date.now() < Date.parse('2026-08-31T23:00:00Z'));
      if (!active) return {};
      const node = this.root.closest('section[id^="MainProduct-"]')?.querySelector('script[id^="SummerPdpData-"]');
      if (!node) return {};
      try { return JSON.parse(node.textContent).rates || {}; } catch (error) { return {}; }
    }
    labelPrice(input) {
      const label = input?.nextElementSibling;
      return label?.classList.contains('variant-label') ? label.querySelector('.variant-price') : null;
    }
    quoteState(input, state, rates) {
      const octoberRows = window.BeleaevOctoberPromo?.[this.data.handle]; /*
        'caviar-brunch-box': [[7695, 6541, 15]],
        'happy-birthday-hamper': [[7753, 6590, 15]],
        'the-grand-caviar-board': [[25575, 21739, 15]],
        'beluga-caviar-gift-board': [[25575, 21739, 15]],
        'the-beleaev-signature-tasting-set-4x125g': [[99900, 84915, 15]],
        'the-beleaev-signature-tasting-set': [[24700, 20995, 15]],
        'the-beleaev-signature-tasting-set-4x50g': [[39900, 33915, 15]],
      */
      const variant = this.data.variants.find(item => item.id === String(input?.value));
    const octoberRow = state.mode === 'one-time' && Array.isArray(octoberRows) ? octoberRows.find(row => Number(row?.[0]) === variant?.price) : null;
      const octoberCents = octoberRow ? Number(octoberRow[1]) : null;
      const merchant = Number.isSafeInteger(octoberCents) && octoberCents > 0 ? String(octoberCents) : this.labelPrice(input)?.getAttribute('data-merchant-sale-cents');
      const campaign = window.BeleaevWagyuLaunch;
      const offer = campaign?.active && campaign.offers?.find(item => item.handles.includes(this.data.handle));
      return { ...state, variantId: input?.value, merchantCents: merchant === null ? null : Number(merchant),
        campaignRate: Number(offer?.discount || 0), summerRate: Number(rates[input?.value] || 0) };
    }
    update() {
      if (!this.root?.isConnected) return;
      const checked = this.root.querySelector('.variant-input:checked');
      const selected = checked || (this.data.variants.length === 1 ? {
        value: this.data.variants[0].id,
        getAttribute: name => name === 'form' ? this.root.querySelector('beleaev-purchase-options')?.dataset.formId : null,
      } : null);
      const state = this.purchaseState(selected);
      const rates = this.summerRates();
      const quote = selectedQuote(this.data, this.quoteState(selected, state, rates));
      if (!quote) { this.release(); return; }
      this.renderPrice(quote, state);
      this.root.querySelectorAll('.variant-input').forEach(input => {
        const price = this.labelPrice(input);
        const variant = this.data.variants.find(item => item.id === input.value);
        if (!price || !variant) return;
        if (!this.originals.has(price)) this.originals.set(price, price.textContent);
        const rowState = this.quoteState(input, state, rates);
        const row = variantQuote(variant, rowState) || variantQuote(variant, { ...rowState, mode: 'one-time' });
        const value = this.money(row.price);
        if (price.textContent !== value) price.textContent = value;
      });
      this.markLegacyPrices();
      this.root.dataset.blvPriceOwner = 'allocation';
      this.hidden = false;
      this.updatePerServing(selected, quote.price);
      this.updateSummerOption(selected, state, rates);
      const signature = [quote.variant.id, quote.price, state.mode, state.planId].join(':');
      if (this.signature !== signature) {
        this.signature = signature;
        this.root.dispatchEvent(new CustomEvent('beleaev:display-price-change', { bubbles: true }));
      }
    }
    markLegacyPrices() {
      this.root.querySelectorAll('[id^="price-"] .price__container, .beleaev-sub-line, .beleaev-price-bottom, .beleaev-total, .beleaev-total-tasting, .blv-summer-pdp-price')
        .forEach(node => {
          if (node.closest('product-info, [id^="ProductInfo-"]') !== this.root ||
            node.closest('quick-add-modal, product-recommendations, .card, .card-wrapper')) return;
          if (!node.classList.contains('blv-allocation-legacy-fragment')) node.classList.add('blv-allocation-legacy-fragment');
        });
    }
    renderPrice(quote, state) {
      if (!this.amount) {
        const line = document.createElement('div');
        line.className = 'blv-allocation-price__line';
        this.amount = document.createElement('span');
        this.amount.className = 'blv-allocation-price__amount';
        this.amount.setAttribute('data-blv-allocation-amount', '');
        this.compare = document.createElement('s');
        this.compare.className = 'blv-allocation-price__compare';
        line.append(this.compare, this.amount);
        this.append(line);
      }
      const value = this.money(quote.price);
      const original = quote.compare ? this.money(quote.compare) : '';
      if (this.amount.textContent !== value) this.amount.textContent = value;
      if (this.compare.textContent !== original) this.compare.textContent = original;
      this.compare.hidden = !quote.compare;
      this.dataset.unitCents = String(quote.price);
      this.dataset.currency = this.data.currency;
      this.dataset.locale = this.data.locale;
      this.dataset.purchaseMode = state.mode;
    }
    updatePerServing(selected, unitCents) {
      const perServing = this.root.querySelector('.beleaev-per-serving');
      const weight = selected?.nextElementSibling?.querySelector('.variant-weight')?.textContent.trim() ||
        (this.data.variants.length === 1 ? this.root.querySelector('h1')?.textContent.trim() : '') || '';
      const match = weight.match(/\b(\d+(?:[.,]\d+)?)\s*(g|kg)\b/i);
      if (!perServing || !match) return;
      const grams = Number(match[1].replace(',', '.')) * (match[2].toLowerCase() === 'kg' ? 1000 : 1);
      const servings = Math.max(1, Math.floor(grams / 10));
      const amount = perServing.querySelector('.beleaev-per-serving__amount');
      const hint = perServing.querySelector('.beleaev-per-serving__hint');
      const value = this.money(unitCents / servings);
      if (amount && amount.textContent !== value) amount.textContent = value;
      const hintValue = `~${servings} servings`;
      if (hint && hint.textContent !== hintValue) hint.textContent = hintValue;
      if (perServing.parentElement !== this) this.append(perServing);
    }
    updateSummerOption(selected, state, rates) {
      // This card fragment belongs to the theme's seasonal renderer, not Appstle controls.
      const option = this.root.querySelector('.blv-summer-option');
      if (!option) return;
      const oneTimeState = this.quoteState(selected, { ...state, mode: 'one-time' }, rates);
      const quote = selectedQuote(this.data, oneTimeState);
      if (!quote) return;
      const now = option.querySelector('.blv-summer-option__now');
      const was = option.querySelector('.blv-summer-option__was');
      const value = this.money(quote.price);
      const original = quote.compare ? this.money(quote.compare) : '';
      if (now && now.textContent !== value) now.textContent = value;
      if (was && was.textContent !== original) was.textContent = original;
      if (was) was.hidden = !quote.compare;
      if (oneTimeState.merchantCents > 0) {
        const badge = this.root.querySelector('.blv-summer-option__badge');
        const savings = quote.compare ? Math.round((quote.compare - quote.price) * 100 / quote.compare) : 0;
        const label = `SAVE ${savings}%`;
        if (badge && badge.textContent !== label) badge.textContent = label;
        if (badge) badge.hidden = !savings;
      }
    }
    release() {
      if (this.root?.dataset.blvPriceOwner === 'allocation') delete this.root.dataset.blvPriceOwner;
      this.originals?.forEach((value, price) => { if (price.isConnected && price.textContent !== value) price.textContent = value; });
      this.originals?.clear();
      const perServing = this.querySelector('.beleaev-per-serving');
      const host = this.root?.querySelector('[id^="price-"]');
      if (perServing && host) host.after(perServing);
      this.hidden = true;
      if (this.signature) {
        this.signature = '';
        this.root?.dispatchEvent(new CustomEvent('beleaev:display-price-change', { bubbles: true }));
      }
    }
  }
  customElements.define('beleaev-subscription-prices', SubscriptionPrices);
})();
